import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, SimpleChanges, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { EditorModule } from 'primeng/editor';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { DialogModule } from 'primeng/dialog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
export interface EmailData {
    to: string;
    subject: string;
    content: string;
    typedContent?: string;
    attachments?: File[];
}

/** Compositor de e-mail (mock): edita destinatário/assunto/conteúdo com anexos e pré-visualização. */
@Component({
    selector: 'app-email',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, CardModule, EditorModule, IconFieldModule, InputIconModule, InputTextModule, RippleModule, DialogModule],
    templateUrl: './email.component.html',
    styleUrl: './email.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailComponent implements OnChanges, OnDestroy {
    readonly visible = model<boolean>(false);
    readonly dadosIniciais = input<Partial<EmailData>>();

    readonly sendEmail = output<EmailData>();
    readonly goBack = output<void>();
    readonly cancelar = output<void>();
    readonly saveDraft = output<EmailData>();

    emailData: EmailData = {
        to: '',
        subject: '',
        content: '',
        attachments: []
    };
    mensagemUsuario = '';
    exibirDialogPreview = false;
    previewUrl: SafeResourceUrl | null = null;
    private previewObjectUrl: string | null = null;
    private templateHtml = '';

    private readonly sanitizer = inject(DomSanitizer);

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['dadosIniciais'] && this.dadosIniciais()) {
            this.aplicarDadosIniciais();
        }
    }

    ngOnDestroy(): void {
        this.revogarPreviewObjectUrl();
    }

    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            const newFiles = Array.from(input.files);
            const current = this.emailData.attachments ?? [];
            this.emailData.attachments = [...current, ...newFiles];
            // reset the input so selecting the same file again will trigger change
            input.value = '';
        }
    }

    removeAttachment(index: number): void {
        if (!this.emailData.attachments) return;
        this.emailData.attachments = this.emailData.attachments.filter((_, i) => i !== index);
    }

    onSendMessage(): void {
        if (this.isFormValid()) {
            const conteudoFinal = this.montarHtmlFinal();
            this.sendEmail.emit({ ...this.emailData, content: conteudoFinal, typedContent: this.mensagemUsuario });
            // keep dialog open only if parent desires; do not auto close
        }
    }

    onGoBack(): void {
        this.goBack.emit();
        this.closeDialog();
    }

    onCancel(): void {
        this.cancelar.emit();
        this.closeDialog();
    }

    onSaveDraft(): void {
        if (this.isDraftSaveable()) {
            this.saveDraft.emit({ ...this.emailData, content: this.montarHtmlFinal() });
        }
    }

    isFormValid(): boolean {
        return !!(this.emailData.to && this.emailData.subject);
    }

    isDraftSaveable(): boolean {
        return !!(this.emailData.to || this.emailData.subject || this.mensagemUsuario);
    }

    closeDialog(): void {
        if (this.visible()) {
            this.visible.set(false);
        }
    }

    private aplicarDadosIniciais(): void {
        const conteudoInicial = this.normalizarTemplateEditavel(this.dadosIniciais()?.content ?? '');
        this.templateHtml = conteudoInicial;
        this.mensagemUsuario = '';

        this.emailData = {
            to: this.dadosIniciais()?.to ?? '',
            subject: this.dadosIniciais()?.subject ?? '',
            content: '',
            attachments: this.dadosIniciais()?.attachments ?? []
        };
        this.atualizarPreviewHtml();
    }

    private normalizarTemplateEditavel(html: string): string {
        return html
            .replace(/\scontenteditable\s*=\s*(['"])false\1/gi, '')
            .replace(/\sreadonly(\s*=\s*(['"])?readonly\2)?/gi, '')
            .replace(/\sdisabled(\s*=\s*(['"])?disabled\2)?/gi, '');
    }

    onConteudoAlterado(): void {
        this.exibirDialogPreview = false;
    }

    onVisualizar(): void {
        this.atualizarPreviewHtml();
        this.exibirDialogPreview = true;
    }

    private atualizarPreviewHtml(): void {
        this.revogarPreviewObjectUrl();

        const html = this.montarHtmlFinal();
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const objectUrl = URL.createObjectURL(blob);

        this.previewObjectUrl = objectUrl;
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
    }

    private revogarPreviewObjectUrl(): void {
        if (!this.previewObjectUrl) {
            return;
        }

        URL.revokeObjectURL(this.previewObjectUrl);
        this.previewObjectUrl = null;
    }

    private montarHtmlFinal(): string {
        const mensagemHtml = (this.mensagemUsuario ?? '').trim();
        const templateBase = this.templateHtml ?? '';

        if (!mensagemHtml) {
            return templateBase.replace(/\$\{texto\}/g, '');
        }

        if (templateBase.includes('${texto}')) {
            return templateBase.replace(/\$\{texto\}/g, `${mensagemHtml}<br><br>`);
        }

        return `${templateBase}<br><br>${mensagemHtml}`;
    }
}
